/**
 * Calculate the current streak of consecutive days with completed runs
 * @param {Object} runs - Object with date strings as keys and run data as values
 * @returns {number} Current streak in days
 */
export const getCurrentStreak = (runs) => {
  if (!runs || Object.keys(runs).length === 0) return 0;

  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check each day going backwards from today
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() - i);
    const dateString = checkDate.toDateString();
    const run = runs[dateString];

    // Check if there's a completed run on this day
    if (run && (run.status === 'completed' || run.status === 'adjusted')) {
      streak++;
    } else if (i === 0) {
      // If today has no run, check yesterday to see if streak should start from yesterday
      continue;
    } else {
      // Streak broken
      break;
    }
  }

  return streak;
};

/**
 * Calculate the longest streak ever achieved
 * @param {Object} runs - Object with date strings as keys and run data as values
 * @returns {number} Longest streak in days
 */
export const getLongestStreak = (runs) => {
  if (!runs || Object.keys(runs).length === 0) return 0;

  // Get all completed run dates and sort them
  const completedDates = Object.entries(runs)
    .filter(([_, run]) => run.status === 'completed' || run.status === 'adjusted')
    .map(([dateString, _]) => new Date(dateString))
    .sort((a, b) => a - b);

  if (completedDates.length === 0) return 0;

  let longestStreak = 1;
  let currentStreak = 1;

  for (let i = 1; i < completedDates.length; i++) {
    const prevDate = new Date(completedDates[i - 1]);
    const currDate = new Date(completedDates[i]);

    // Calculate difference in days
    const diffTime = currDate - prevDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      // Consecutive day
      currentStreak++;
      longestStreak = Math.max(longestStreak, currentStreak);
    } else {
      // Streak broken
      currentStreak = 1;
    }
  }

  return longestStreak;
};

/**
 * Calculate weekly consistency (consecutive weeks with at least one run or rest day)
 * @param {Object} runs - Object with date strings as keys and run data as values
 * @param {boolean} weekStartsMonday - Whether week starts on Monday
 * @returns {number} Number of consecutive weeks with activity
 */
export const getWeeklyStreak = (runs, weekStartsMonday = true) => {
  if (!runs || Object.keys(runs).length === 0) return 0;

  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check each week going backwards from current week
  for (let weekOffset = 0; weekOffset < 52; weekOffset++) {
    let hasActivity = false;

    // Check all 7 days in this week
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - (weekOffset * 7 + dayOffset));
      const dateString = checkDate.toDateString();
      const run = runs[dateString];

      // Count completed runs, adjusted runs, and rest days as activity
      if (run && (run.status === 'completed' || run.status === 'adjusted' || run.status === 'rest')) {
        hasActivity = true;
        break;
      }
    }

    if (hasActivity) {
      streak++;
    } else if (weekOffset === 0) {
      // If current week has no activity, check previous week
      continue;
    } else {
      // Streak broken
      break;
    }
  }

  return streak;
};
